<script setup>
import { computed, onMounted, ref, watch } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ViewImage from "@/components/ViewImage.vue";
import SearcherFiles from "@/components/gallery-files/SearcherFiles.vue";
import EvidenceDetail from "./EvidenceDetail.vue";
import { evidenceStore } from "@/_store/evidenceStore";
import { argonTableStore } from "@/_store/argonTableStore";
import { userStore } from "@/_store/userStore";
import { errorUtils } from "@/utils";
import { defaultFilterDates } from "@/maestros/defaultDatesValues";
import moment from "moment";

const evidencesStore = evidenceStore();
const tableStore = argonTableStore();
const userStores = userStore();
const isLoading = ref(false);
const selectedEvidenceId = ref(null);
const snackbar = ref(null);
const lightbox = ref(null);
const refFileContainer = ref(null);
const genericContainer = ref(null);
const customFilterDate = ref(evidencesStore.customFilterDate);


const sort = ref({
  _id: -1,
});
const fieldFilters = ref([
  { field: "stop_number", description: "Recogida", exact: true },
  { field: "customer", description: "Cliente" },
]);
const defaultFilters = ref([
  {
    field: "type",
    label: "Tipo",
    multi: false,
    options: [
      { label: "Foto", value: "FOTO" },
      { label: "Firma", value: "FIRMA" },
    ],
  },
  {
    field: "date",
    label: "Fecha",
    multi: false,
    isDate: true,
    options: [
      ...defaultFilterDates,
      {
        label: "Personalizado",
        value: customFilterDate,
        isCustomDate: true,
        storage: evidencesStore,
        formatDate: "DD/MM/YYYY",
      },
    ],
  }
]);

const evidences = computed(() => {
  if (!evidencesStore.evidences) return [];
  if (Array.isArray(evidencesStore.evidences.data)) return evidencesStore.evidences.data;
  if (Array.isArray(evidencesStore.evidences.documents)) return evidencesStore.evidences.documents;
  return [];
});

const total = computed(() => Number(evidencesStore.evidences?.total || 0));
const selectedEvidenceFromStore = computed(() => {
  if (!evidencesStore.evidence) return null;
  return evidencesStore.evidence?.data || evidencesStore.evidence;
});

const selectedEvidence = computed(() => {
  if (!selectedEvidenceId.value) return null;
  if (selectedEvidenceFromStore.value?._id === selectedEvidenceId.value) {
    return selectedEvidenceFromStore.value;
  }
  return evidences.value.find((item) => item?._id === selectedEvidenceId.value) || null;
});

async function fetchEvidences() {
  try {
    isLoading.value = true;
    userStores.filtersPage.evidence = {
      filters: tableStore.filters,
      filtersFavorite: tableStore.filtersFavorite,
    };
    await evidencesStore.getEvidences(tableStore.pagination, tableStore.query, sort.value);
  } catch (error) {
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(error),
      type: "danger",
      color: "white",
    };
  } finally {
    isLoading.value = false;
  }
}

function refresh() {
  fetchEvidences();
}

function filterChanged(changes) {
  selectedEvidenceId.value = null;
  evidencesStore.evidence = null;
  tableStore.changeFilters(changes);
  fetchEvidences();
}

function reloadPage() {
  fetchEvidences();
}

function selectEvidence(evidence) {
  if (!evidence?._id) return;
  if (selectedEvidenceId.value === evidence._id) {
    selectedEvidenceId.value = null;
    evidencesStore.evidence = null;
    return;
  }
  selectedEvidenceId.value = evidence._id;
  evidencesStore.evidence = null;
  evidencesStore.getEvidenceById(evidence._id);
}

function openLightbox(evidence) {
  if (!evidence?.path) return;
  lightbox.value = {
    url: evidence.path,
    label: getFileName(evidence.path),
  };
  selectEvidence(evidence);
}

function closeLightbox() {
  lightbox.value = null;
}

function closeSnackbar() {
  snackbar.value = null;
}

function getFileName(path) {
  const value = String(path || "").trim();
  if (!value) return "documento";
  const cleanPath = value.split("?")[0];
  const parts = cleanPath.split("/").filter(Boolean);
  return decodeURIComponent(parts[parts.length - 1] || "documento");
}

function downloadSelected() {
  if (!selectedEvidence.value?.path) return;
  const link = document.createElement("a");
  link.href = selectedEvidence.value.path;
  link.setAttribute("download", getFileName(selectedEvidence.value.path));
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

watch(
  () => evidencesStore.error,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);

watch(
  () => evidences.value,
  () => {
    if (!selectedEvidenceId.value) return;
    const stillExists = evidences.value.some((item) => item?._id === selectedEvidenceId.value);
    if (!stillExists) {
      selectedEvidenceId.value = null;
      evidencesStore.evidence = null;
    }
  },
  { deep: true }
);

onMounted(() => {
  evidencesStore.clear();
  tableStore.clear();
  tableStore.pagination.limit = 40;
  const savedFilters = userStores.filtersPage.evidence?.filters || [];
  tableStore.changeFilters(savedFilters);
  fetchEvidences();
});
</script>

<template>
  <div class="card evidence-gallery-shell">
    <div class="card-body">

  <searcher-files
    :field-filters="fieldFilters"
    :default-filters="defaultFilters"
    :total="total"
    :show-new-button="false"
    :show-config-option="false"
    @filter-changed="filterChanged"
    @on-reload="refresh"
    @page-changed="reloadPage"
  />

  <div class="row mt-3 card-detail-layout">
    <div class="col-12">
      <div class="row evidence-gallery-content">
        <div class="col-xl-10 col-lg-8 col-md-7 col-sm-6 d-flex">
          <div
            id="id-card"
            ref="refFileContainer"
            class="card h-100 w-100 customer"
            :class="{ loading: isLoading }"
          >
            <div
              id="id-card-body"
              ref="genericContainer"
              class="card-body pt-2"
            >
              <div class="gallery-files">
                <div v-if="evidences.length > 0" class="gallery-files-container">
                  <view-image
                    v-for="evidence in evidences"
                    :key="evidence._id"
                    class="evidencia-card hover-pointer"
                    :class="{ active: selectedEvidenceId === evidence._id }"
                    :url="evidence.path"
                    :label="evidence.type + ' · ' + moment(evidence.date).format('DD/MM/YYYY')"
                    @click="selectEvidence(evidence)"
                    @view="openLightbox(evidence)"
                  />
                </div>

                <div v-else-if="!isLoading" class="row my-3">
                  <div class="col-lg-12 text-center">
                    <span class="label-placeholder text-lg">Sin evidencias</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-2 col-lg-4 col-md-5 col-sm-6 d-flex">
          <evidence-detail
            class="w-100 h-100"
            :selected-evidence="selectedEvidence"
            :total="total"
            @download="downloadSelected"
          />
        </div>
      </div>
    </div>
  </div>
      
    </div>
  </div>
  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>

  <teleport to="body">
    <div v-if="lightbox" class="evidence-lightbox-overlay" @click="closeLightbox">
      <div class="evidence-lightbox-box" @click.stop>
        <button class="evidence-lightbox-close" @click="closeLightbox">
          <i class="fas fa-times"></i>
        </button>
        <img :src="lightbox.url" :alt="lightbox.label" class="evidence-lightbox-img" />
        <p class="evidence-lightbox-caption">{{ lightbox.label }}</p>
      </div>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
.evidence-gallery-shell {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.evidence-gallery-shell > .card-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.evidence-gallery-shell > .card-body > .card-detail-layout {
  flex: 1 1 auto;
  min-height: 0;
}

.evidence-gallery-shell > .card-body > .card-detail-layout > .col-12 {
  height: 100%;
}

.evidence-gallery-content {
  height: 100%;
  min-height: 0;
}

.evidence-gallery-content > [class*="col-"] {
  min-height: 0;
}

.hover-pointer {
  cursor: pointer;
}

.gallery-files {
  min-height: 100%;
}

#id-card-body {
  position: relative;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-inline: 0.85rem;
}

.gallery-files-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.8rem;
  align-items: start;
}

#id-card.loading #id-card-body::after {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("../../../assets/img/loading.svg");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 32px;
  background-color: rgba(255, 255, 255, 0.5);
  content: "";
  border-radius: 0.5rem;
}

.evidencia-card {
  border-radius: 8px;
  transition: box-shadow 0.15s;
  cursor: pointer;
  overflow: hidden;
}

:deep(.gallery-files-container .view-image-card) {
  width: 100%;
  max-width: 100%;
}

:deep(.evidencia-card.active.view-image-card) {
  border-color: var(--primary);
   
  box-shadow: 0 0 0 3px var(--primary);
}

:deep(.argon-searcher) {
  min-height: 34px;
}

:deep(.argon-searcher .searcher) {
  min-height: 34px;
}

</style>

<style>
.evidence-lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.evidence-lightbox-box {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90vw;
  max-height: 90vh;
}

.evidence-lightbox-close {
  position: absolute;
  top: -2.25rem;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
  padding: 0.25rem;
  opacity: 0.85;
  transition: opacity 0.15s;
}

.evidence-lightbox-close:hover {
  opacity: 1;
}

.evidence-lightbox-img {
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 6px;
  display: block;
}

.evidence-lightbox-caption {
  margin-top: 0.65rem;
  color: #ffffff;
  font-size: 0.8rem;
  text-align: center;
  max-width: 80vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
